import {ACTIVITY_VERBS} from '../constants/activity';
import {camelizeProperties} from './jsonapi-helpers';
import {DOCUMENT_ATTACHMENT_TYPE, FILE_ATTACHMENT_TYPE, PHOTO_ATTACHMENT_TYPE} from '../constants/attachments';
import {ISSUE_ATTRIBUTE_NAMES} from '../constants/issue-attribute-names';
import {isDocsAttachment, isImageAttachment} from './attachmentsHelper';
import {v4 as getUUID} from 'uuid';
import partition from 'lodash/partition';

const parseAttachmentActivity = activity => {
  const clientID = getUUID();
  const imageObj = {clientID};

  activity.changes.forEach(change => {
    imageObj[change.property] = change.after;
  });

  // In case we get deleted_at we don't have anything to do with it.
  // So we just ignore that entire attachment. (for now - until BE changes the response accordingly).
  if (imageObj.deleted_at) {
    return null;
  }

  // The only condition at the moment where we can't display the attachment is when we only have a name.
  // We need more than just the url in order to display it (urn for instance) but the URL is the main reason.
  imageObj.hasURL = !Object.keys(imageObj).reduce((onlyName, prop) => onlyName && prop === 'name', true);

  return {
    type: activity.type,
    datetime: activity.datetime,
    id: clientID,
    attributes: {
      // We're keeping the non camelCase properties for backward compatibility.
      // This should be changed but is too risky right now before the release.
      // I've opened a ticket to remove the backward compatibility properties - BTLV-7259
      ...camelizeProperties(imageObj, imageObj)
    }
  };
};

export const parseActivity = activities => {
  const partitionedActivities = partition(activities, activity => activity.type !== ACTIVITY_VERBS.ATTACH);
  // By partitioning based on an activity is an attachment or not and assigning first the non-attachments, we're making sure that non-attachment activities will always be before attachments activities in the activity block.
  const activitiesArray = partitionedActivities[0];

  // Going over attachment activities
  if (partitionedActivities[1].length) {
    const docsChanges = [];
    const photosChanges = [];
    const filesChanges = [];
    partitionedActivities[1].forEach(attachmentActivity => {
      const attachmentChange = parseAttachmentActivity(attachmentActivity);
      if (isDocsAttachment(attachmentChange.attributes)) {
        docsChanges.push(attachmentChange);
      } else if (isImageAttachment(attachmentChange.attributes)) {
        photosChanges.push(attachmentChange);
      } else {
        filesChanges.push(attachmentChange);
      }
    });

    if (photosChanges.length) {
      activitiesArray.push({
        type: photosChanges[0].type,
        datetime: photosChanges[0].datetime,
        changes: [
          {
            property: ISSUE_ATTRIBUTE_NAMES.ATTACHMENT,
            attachmentType: PHOTO_ATTACHMENT_TYPE,
            clientID: photosChanges[0].id,
            after: photosChanges
          }
        ]
      });
    }

    if (docsChanges.length) {
      activitiesArray.push({
        type: docsChanges[0].type,
        datetime: docsChanges[0].datetime,
        changes: [
          {
            property: ISSUE_ATTRIBUTE_NAMES.ATTACHMENT,
            attachmentType: DOCUMENT_ATTACHMENT_TYPE,
            clientID: docsChanges[0].id,
            after: docsChanges
          }
        ]
      });
    }

    if (filesChanges.length) {
      activitiesArray.push({
        type: filesChanges[0].type,
        datetime: filesChanges[0].datetime,
        changes: [
          {
            property: ISSUE_ATTRIBUTE_NAMES.ATTACHMENT,
            attachmentType: FILE_ATTACHMENT_TYPE,
            clientID: filesChanges[0].id,
            after: filesChanges
          }
        ]
      });
    }
  }

  return activitiesArray;
};

export const getActivityBlocksAttachments = (activityBlocks = []) => {
  const attachments = [];

  activityBlocks.forEach(activityBlock => {
    activityBlock.activityList.forEach(activity => {
      activity.changes.forEach(change => {
        if (change.property === ISSUE_ATTRIBUTE_NAMES.ATTACHMENT && change.after) {
          change.after.forEach(picture => {
            attachments.push(picture.attributes);
          });
        }
      });
    });
  });
  return attachments;
};

export const getChangesFromActivity = activity => {
  const changes = [];
  const customAttributesProperty = ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES;
  activity.changes.forEach(change => {
    if (change.property !== customAttributesProperty) {
      changes.push(change);
    } else {
      change.after.forEach(customAttributeChange =>
        changes.push(Object.assign({property: customAttributesProperty}, customAttributeChange))
      );
    }
  });
  return changes;
};
